import { differenceInMinutes } from "date-fns";

import { setDayFromWorkdayTimeString } from "../../utils/dateUtils";

const DEFAULT_DAY_VIEW_CLUSTER_THRESHOLD = 5;
const MIN_DAY_VIEW_CLUSTER_THRESHOLD = 2;
const MIN_DAY_VIEW_EVENT_WIDTH = 120;
// 50 min → 40px min card height at slotHeight 48, which clears the
// `height < 36` compact threshold so every card (short or long duration)
// shows number + status and reads as the same card. Grouping/lane-packing
// derive from the same value, so close events pack side-by-side rather than
// overlapping. ponytail: display-only clamp; real durations still drive
// scheduling/capacity elsewhere.
export const MIN_EVENT_DISPLAY_MINUTES = 50;
export const MIN_EVENT_DISPLAY_HEIGHT = 40;
export const OVERLAP_EVENT_GAP = 4;

export const getEventLayoutDurationMinutes = (event) => {
  const rawDuration = Number(event?.eventType?.duration);
  if (Number.isFinite(rawDuration) && rawDuration > 0) {
    return rawDuration;
  }
  return MIN_EVENT_DISPLAY_MINUTES;
};

export const getEventDisplayDurationMinutes = (event) =>
  Math.max(getEventLayoutDurationMinutes(event), MIN_EVENT_DISPLAY_MINUTES);

export const getEventDisplayHeight = (event, slotHeight = 48) =>
  (getEventDisplayDurationMinutes(event) / 60) * slotHeight;

export const getSingleEventRenderHeight = (event, groupHeight, slotHeight) =>
  Math.max(groupHeight, getEventDisplayHeight(event, slotHeight));

const getEventDisplayEnd = (event, start) =>
  new Date(start.getTime() + getEventDisplayDurationMinutes(event) * 60000);

// Helper: Group visually overlapping events for a given day.
export const groupOverlappingEvents = (events, day) => {
  const sorted = [...events].sort((a, b) => {
    const aStart = setDayFromWorkdayTimeString(day, a.time);
    const bStart = setDayFromWorkdayTimeString(day, b.time);
    return (
      aStart - bStart ||
      getEventDisplayEnd(a, aStart) - getEventDisplayEnd(b, bStart)
    );
  });

  const groups = [];
  let currentGroup = [];
  let currentGroupEnd = null;

  sorted.forEach((event) => {
    const eventStart = setDayFromWorkdayTimeString(day, event.time);
    const eventEnd = getEventDisplayEnd(event, eventStart);

    if (currentGroup.length === 0) {
      currentGroup.push(event);
      currentGroupEnd = eventEnd;
    } else if (eventStart < currentGroupEnd) {
      currentGroup.push(event);
      if (eventEnd > currentGroupEnd) {
        currentGroupEnd = eventEnd;
      }
    } else {
      groups.push({
        events: currentGroup,
        groupStart: setDayFromWorkdayTimeString(day, currentGroup[0].time),
        groupEnd: currentGroupEnd,
      });
      currentGroup = [event];
      currentGroupEnd = eventEnd;
    }
  });

  if (currentGroup.length > 0) {
    groups.push({
      events: currentGroup,
      groupStart: setDayFromWorkdayTimeString(day, currentGroup[0].time),
      groupEnd: currentGroupEnd,
    });
  }

  return groups;
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

export const buildDayViewLaneLayout = (events, day, groupStart, slotHeight) => {
  const sorted = [...events].sort((a, b) => {
    const aStart = setDayFromWorkdayTimeString(day, a.time);
    const bStart = setDayFromWorkdayTimeString(day, b.time);
    return aStart - bStart;
  });

  const laneEnds = [];
  const layouts = sorted.map((event) => {
    const start = setDayFromWorkdayTimeString(day, event.time);
    const end = getEventDisplayEnd(event, start);
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = end;

    return {
      event,
      start,
      end,
      lane,
      top: (differenceInMinutes(start, groupStart) / 60) * slotHeight,
      height: getEventDisplayHeight(event, slotHeight),
    };
  });

  const laneCount = Math.max(laneEnds.length, 1);
  return layouts.map((layout) => {
    let span = 1;
    while (layout.lane + span < laneCount) {
      const candidateLane = layout.lane + span;
      let laneIsFree = true;
      for (const other of layouts) {
        if (
          other !== layout &&
          other.lane === candidateLane &&
          rangesOverlap(layout.start, layout.end, other.start, other.end)
        ) {
          laneIsFree = false;
          break;
        }
      }
      if (!laneIsFree) break;
      span += 1;
    }

    const width = `calc(${(span / laneCount) * 100}% - ${
      ((laneCount - span) / laneCount) * OVERLAP_EVENT_GAP
    }px)`;
    const left = `calc(${(layout.lane / laneCount) * 100}% + ${
      (layout.lane / laneCount) * OVERLAP_EVENT_GAP
    }px)`;

    return { ...layout, width, left };
  });
};

export const getResponsiveDayViewClusterThreshold = (
  availableWidth,
  {
    fallbackThreshold = DEFAULT_DAY_VIEW_CLUSTER_THRESHOLD,
    minThreshold = MIN_DAY_VIEW_CLUSTER_THRESHOLD,
    minEventWidth = MIN_DAY_VIEW_EVENT_WIDTH,
  } = {},
) => {
  if (
    !Number.isFinite(availableWidth) ||
    availableWidth <= 0 ||
    !Number.isFinite(minEventWidth) ||
    minEventWidth <= 0
  ) {
    return fallbackThreshold;
  }

  return Math.max(minThreshold, Math.floor(availableWidth / minEventWidth));
};

export const getTooltipAnchorStyle = ({
  inPopover = false,
  top,
  left,
  width,
  height,
} = {}) => {
  if (inPopover) {
    return { display: "block" };
  }

  return {
    position: "absolute",
    top,
    ...(width ? { width, left: left ?? 4, right: undefined } : { left: left ?? 4, right: 4 }),
    height,
    display: "block",
  };
};

export const isEmptySlotDayDisabled = ({
  eventType,
  day,
  daySchedule,
  schedulingOverride = false,
  shouldDisableDateForSchedule,
}) => {
  if (!eventType?.id || !day || !shouldDisableDateForSchedule) return true;
  if (!schedulingOverride && !daySchedule?.selected) return true;
  return shouldDisableDateForSchedule(eventType, day, schedulingOverride);
};
